<template>
  <div class="app-frame" :class="{ 'app-frame--vecura': isVecuraRoute }">
    <MobileHeader
      v-if="isMobileLayout"
      :active-section="activeSection"
      :current-path="currentPath"
      :is-vecura="isVecuraRoute"
    />
    <SiteHeader
      v-else
      :active-section="activeSection"
      :current-path="currentPath"
      :is-vecura="isVecuraRoute"
    />
    <main>
      <Transition name="page-shift" mode="out-in">
        <MobileVecuraPage
          v-if="isVecuraRoute && isMobileLayout"
          key="mobile-vecura"
        />
        <VecuraPage v-else-if="isVecuraRoute" key="vecura" />
        <MorePage v-else-if="isMoreRoute" key="more" />
        <LegalPage
          v-else-if="isLegalRoute"
          :key="legalPage || 'legal'"
          :page="legalPage || 'impressum'"
        />
        <MobileHomePage v-else-if="isMobileLayout" key="mobile-home" />
        <HomePage v-else key="home" />
      </Transition>
    </main>
    <SiteFooter />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import HomePage from "@/components/HomePage.vue";
import LegalPage from "@/components/LegalPage.vue";
import MorePage from "@/components/MorePage.vue";
import MobileHeader from "@/components/MobileHeader.vue";
import MobileHomePage from "@/components/MobileHomePage.vue";
import MobileVecuraPage from "@/components/MobileVecuraPage.vue";
import SiteHeader from "@/components/SiteHeader.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import VecuraPage from "@/components/VecuraPage.vue";
import defaultFavicon from "@/assets/rothdeutsch-favicon.png";
import vecuraFavicon from "@/assets/rothdeutsch-favicon-vecura.png";
import { profile } from "@/data/profile";

type ActiveSection =
  | "profile"
  | "vecura"
  | "expertise"
  | "contact"
  | "more"
  | "legal";
type LegalPageType = "impressum" | "datenschutz";
type ScrollTargetBehavior = "instant" | "smooth";

interface PageMeta {
  title: string;
  description: string;
}

type StructuredData = Record<string, unknown>;

const SITE_ORIGIN = "https://timo-rothdeutsch.de";
const MOBILE_LAYOUT_QUERY = "(max-width: 760px)";
const DESKTOP_HEADER_SCROLL_OFFSET = 104;
const MOBILE_HEADER_SCROLL_OFFSET = 78;
const SOCIAL_IMAGE_PATH = "/img/icons/android-chrome-512x512.png";
const REVEAL_SELECTOR = [
  ".hero__content",
  ".section",
  ".profile-focus__list article",
  ".responsibility__item",
  ".ai-expertise__grid article",
  ".work-modes__list article",
  ".evidence__list article",
  ".contact__channel",
  ".more-page__hero",
  ".more-page__section",
  ".vecura-hero__role",
  ".vecura-pitch__proof article",
  ".vecura-capabilities__grid article",
  ".vecura-case li",
  ".legal-page__section",
  ".mobile-hero",
  ".mobile-section",
  ".mobile-flow article",
  ".mobile-accordion",
  ".mobile-ai__cards article",
  ".mobile-card-list article",
  ".mobile-evidence__timeline article",
  ".mobile-contact-action",
  ".mobile-vecura__hero",
  ".mobile-vecura__section",
  ".mobile-vecura__stats article",
  ".mobile-vecura__capability",
].join(", ");
const currentPath = ref(getCurrentPath());
const activeSection = ref<ActiveSection>("profile");
const isMobileLayout = ref(getMobileLayoutMatches());
let revealObserver: IntersectionObserver | null = null;
let revealRefreshTimer: number | null = null;
let mobileMediaQuery: MediaQueryList | null = null;

const isVecuraRoute = computed(
  () => normalizePath(currentPath.value) === "/vecura"
);
const isMoreRoute = computed(
  () => normalizePath(currentPath.value) === "/mehr"
);
const legalPage = computed<LegalPageType | null>(() => {
  const path = normalizePath(currentPath.value);

  if (path === "/impressum") {
    return "impressum";
  }

  if (path === "/datenschutz") {
    return "datenschutz";
  }

  return null;
});
const isLegalRoute = computed(() => legalPage.value !== null);

function normalizePath(path: string): string {
  const pathOnly = path.split("#")[0];
  const normalized = pathOnly.replace(/\/+$/, "");
  return normalized || "/";
}

function syncCurrentPath(): void {
  currentPath.value = getCurrentPath();
  syncActiveSection();
}

function getCurrentPath(): string {
  return `${window.location.pathname || "/"}${window.location.hash || ""}`;
}

function getMobileLayoutMatches(): boolean {
  if (typeof window === "undefined" || !("matchMedia" in window)) {
    return false;
  }

  return window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
}

function getHeaderScrollOffset(): number {
  return isMobileLayout.value
    ? MOBILE_HEADER_SCROLL_OFFSET
    : DESKTOP_HEADER_SCROLL_OFFSET;
}

function getActiveSectionTriggerOffset(): number {
  return getHeaderScrollOffset() + (isMobileLayout.value ? 98 : 180);
}

function scrollInstantlyTo(top: number): void {
  const root = document.documentElement;
  const previousScrollBehavior = root.style.scrollBehavior;

  // Route changes should not inherit the global smooth-scroll behavior.
  root.style.scrollBehavior = "auto";
  window.scrollTo({ top, left: 0, behavior: "auto" });
  root.style.scrollBehavior = previousScrollBehavior;
}

function scrollToPosition(top: number, behavior: ScrollTargetBehavior): void {
  if (behavior === "instant") {
    scrollInstantlyTo(top);
    return;
  }

  window.scrollTo({ top, left: 0, behavior: "smooth" });
}

function scrollToTarget(
  hash: string,
  behavior: ScrollTargetBehavior = "smooth"
): void {
  if (!hash) {
    scrollToPosition(0, behavior);
    syncActiveSection();
    return;
  }

  const target = document.querySelector<HTMLElement>(hash);

  if (target) {
    const targetTop = target.getBoundingClientRect().top + window.scrollY;

    scrollToPosition(
      Math.max(targetTop - getHeaderScrollOffset(), 0),
      behavior
    );
  }
}

function scheduleRouteScroll(hash: string): void {
  requestAnimationFrame(() => {
    if (hash) {
      scrollToTarget(hash, "smooth");
      window.setTimeout(() => scrollToTarget(hash, "instant"), 360);
      window.setTimeout(syncActiveSection, 440);
      scheduleRevealMotion(380);
      return;
    }

    scrollToTarget("", "instant");
    window.setTimeout(syncActiveSection, 120);
    scheduleRevealMotion(120);
  });
}

function scheduleRevealMotion(delay = 0): void {
  if (revealRefreshTimer !== null) {
    window.clearTimeout(revealRefreshTimer);
  }

  revealRefreshTimer = window.setTimeout(() => {
    revealRefreshTimer = null;
    syncRevealMotion();
  }, delay);
}

function syncRevealMotion(): void {
  const reduceMotion =
    "matchMedia" in window &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>(REVEAL_SELECTOR)
  );

  elements.forEach((element, index) => {
    if (!element.classList.contains("motion-reveal")) {
      element.classList.add("motion-reveal");
      element.style.setProperty("--motion-order", String(index % 5));
    }

    if (reduceMotion || !revealObserver) {
      element.classList.add("motion-reveal--visible");
      return;
    }

    revealObserver.observe(element);
  });
}

function syncActiveSection(): void {
  if (isLegalRoute.value) {
    activeSection.value = "legal";
    return;
  }

  if (isVecuraRoute.value) {
    activeSection.value = "vecura";
    return;
  }

  if (isMoreRoute.value) {
    activeSection.value = "more";
    return;
  }

  const pageBottom =
    document.documentElement.scrollHeight - window.innerHeight - 8;

  if (isMobileLayout.value && window.scrollY >= pageBottom) {
    activeSection.value = "contact";
    return;
  }

  const trackedSections: Array<{
    id: string;
    section: Exclude<ActiveSection, "vecura">;
  }> = [
    {
      id: "expertise",
      section: "expertise",
    },
    {
      id: "contact",
      section: "contact",
    },
  ];
  let nextSection: Exclude<ActiveSection, "vecura"> = "profile";

  trackedSections.forEach(({ id, section }) => {
    const element = document.getElementById(id);

    if (
      element &&
      element.getBoundingClientRect().top <= getActiveSectionTriggerOffset()
    ) {
      nextSection = section;
    }
  });

  activeSection.value = nextSection;
}

async function navigateWithinSite(href: string): Promise<void> {
  const nextUrl = new URL(href, window.location.origin);
  const nextPath = `${nextUrl.pathname}${nextUrl.hash}`;

  window.history.pushState({}, "", nextPath);
  syncCurrentPath();

  await nextTick();
  scheduleRouteScroll(nextUrl.hash);
}

function handlePopState(): void {
  syncCurrentPath();

  nextTick().then(() => {
    scheduleRouteScroll(window.location.hash);
  });
}

function syncMobileLayout(event?: MediaQueryListEvent): void {
  isMobileLayout.value =
    event?.matches ?? mobileMediaQuery?.matches ?? getMobileLayoutMatches();
}

function bindMobileLayout(): void {
  if (typeof window === "undefined" || !("matchMedia" in window)) {
    return;
  }

  mobileMediaQuery = window.matchMedia(MOBILE_LAYOUT_QUERY);
  syncMobileLayout();

  if ("addEventListener" in mobileMediaQuery) {
    mobileMediaQuery.addEventListener("change", syncMobileLayout);
    return;
  }

  mobileMediaQuery.addListener(syncMobileLayout);
}

function unbindMobileLayout(): void {
  if (!mobileMediaQuery) {
    return;
  }

  if ("removeEventListener" in mobileMediaQuery) {
    mobileMediaQuery.removeEventListener("change", syncMobileLayout);
    return;
  }

  mobileMediaQuery.removeListener(syncMobileLayout);
}

function handleDocumentClick(event: MouseEvent): void {
  if (
    event.defaultPrevented ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return;
  }

  const target = event.target as HTMLElement | null;
  const link = target?.closest("a");

  if (!link) {
    return;
  }

  const href = link.getAttribute("href");
  const targetAttribute = link.getAttribute("target");

  if (
    !href ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    targetAttribute === "_blank"
  ) {
    return;
  }

  const nextUrl = new URL(href, window.location.origin);

  if (nextUrl.origin !== window.location.origin) {
    return;
  }

  event.preventDefault();
  navigateWithinSite(href);
}

function syncFavicon(): void {
  const faviconHref = isVecuraRoute.value ? vecuraFavicon : defaultFavicon;
  let favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']");

  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }

  favicon.type = "image/png";
  favicon.href = faviconHref;
}

function syncDocumentTheme(): void {
  const vecuraClass = "document-theme--vecura";

  document.documentElement.classList.toggle(vecuraClass, isVecuraRoute.value);
  document.body.classList.toggle(vecuraClass, isVecuraRoute.value);
}

function getPageMeta(): PageMeta {
  const path = normalizePath(currentPath.value);

  if (path === "/vecura") {
    return {
      title: "Vecura - RAG-Plattform für Fachwissen | Timo Rothdeutsch",
      description:
        "Vecura macht komplexes Unternehmens- und Fachwissen über eine produktionsnahe RAG-Plattform nutzbar: mit Quellenbezug, Retrieval und Integration.",
    };
  }

  if (path === "/impressum") {
    return {
      title: "Impressum | Timo Rothdeutsch",
      description:
        "Anbieterkennzeichnung und Kontaktangaben für die Website von Timo Rothdeutsch.",
    };
  }

  if (path === "/datenschutz") {
    return {
      title: "Datenschutz | Timo Rothdeutsch",
      description:
        "Datenschutzhinweise zur Website von Timo Rothdeutsch und zu Kontaktwegen wie E-Mail, LinkedIn und Vecura.",
    };
  }

  if (path === "/mehr") {
    return {
      title: "Info | Timo Rothdeutsch",
      description:
        "Kurzprofil, persönlicher Kontext sowie Impressum und Datenschutz zur Website von Timo Rothdeutsch.",
    };
  }

  return {
    title: "Timo Rothdeutsch - KI, Architektur und technische Verantwortung",
    description:
      "Timo Rothdeutsch arbeitet an KI- und Wissenssystemen, RAG-Architektur, technischer Verantwortung und Aufbauarbeit als Mitgründer und COO bei Vecura.",
  };
}

function syncDocumentMeta(): void {
  const meta = getPageMeta();
  const pageUrl = new URL(window.location.pathname, SITE_ORIGIN);
  const socialImageUrl = new URL(SOCIAL_IMAGE_PATH, SITE_ORIGIN);

  document.documentElement.lang = "de";
  document.title = meta.title;
  upsertMeta("name", "description", meta.description);
  upsertMeta("property", "og:title", meta.title);
  upsertMeta("property", "og:description", meta.description);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", pageUrl.toString());
  upsertMeta("property", "og:image", socialImageUrl.toString());
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", meta.title);
  upsertMeta("name", "twitter:description", meta.description);
  upsertMeta("name", "twitter:image", socialImageUrl.toString());
  upsertMeta(
    "name",
    "theme-color",
    isVecuraRoute.value ? "#0e151c" : "#f4f1ea"
  );
  upsertCanonical(pageUrl.toString());
  upsertStructuredData(getStructuredData(meta, pageUrl.toString()));
}

function getStructuredData(meta: PageMeta, pageUrl: string): StructuredData {
  const path = normalizePath(currentPath.value);
  const personId = `${SITE_ORIGIN}/#person`;
  const websiteId = `${SITE_ORIGIN}/#website`;
  const pageId = `${pageUrl}#webpage`;
  const vecuraId = "https://vecura.io/#organization";
  const isVecuraPage = path === "/vecura";
  const pageType = path === "/" ? "ProfilePage" : "WebPage";

  const graph: StructuredData[] = [
    {
      "@type": "Person",
      "@id": personId,
      name: profile.name,
      url: SITE_ORIGIN,
      email: `mailto:${profile.email}`,
      jobTitle: "Mitgründer & COO bei Vecura",
      sameAs: [profile.linkedinUrl],
      worksFor: {
        "@id": vecuraId,
      },
      knowsAbout: [
        "Softwarearchitektur",
        "KI-Systeme",
        "Retrieval-Augmented Generation",
        "RAG-Architektur",
        "Anforderungsanalyse",
        "Technische Verantwortung",
        "Produktaufbau",
      ],
    },
    {
      "@type": "Organization",
      "@id": vecuraId,
      name: "Vecura",
      url: "https://vecura.io",
      sameAs: ["https://www.linkedin.com/company/vecura-gmbh"],
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: SITE_ORIGIN,
      name: profile.name,
      publisher: {
        "@id": personId,
      },
      inLanguage: "de",
    },
    {
      "@type": pageType,
      "@id": pageId,
      url: pageUrl,
      name: meta.title,
      description: meta.description,
      inLanguage: "de",
      isPartOf: {
        "@id": websiteId,
      },
      about: {
        "@id": isVecuraPage ? vecuraId : personId,
      },
      mainEntity: {
        "@id": isVecuraPage ? vecuraId : personId,
      },
    },
  ];

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

function upsertMeta(
  attribute: "name" | "property",
  key: string,
  content: string
): void {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`
  );

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
}

function upsertCanonical(href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(
    "link[rel='canonical']"
  );

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = href;
}

function upsertStructuredData(data: StructuredData): void {
  const scriptId = "structured-data";
  let element = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement("script");
    element.id = scriptId;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

onMounted(() => {
  bindMobileLayout();

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  if ("IntersectionObserver" in window) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("motion-reveal--visible");
          revealObserver?.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.12,
      }
    );
  }

  window.addEventListener("popstate", handlePopState);
  window.addEventListener("hashchange", handlePopState);
  window.addEventListener("scroll", syncActiveSection, { passive: true });
  window.addEventListener("resize", syncActiveSection);
  document.addEventListener("click", handleDocumentClick);
  syncFavicon();
  syncDocumentTheme();
  syncDocumentMeta();

  nextTick().then(() => {
    if (window.location.hash) {
      scrollToTarget(window.location.hash);
    }

    syncActiveSection();
    scheduleRevealMotion(60);
  });
});

watch(isVecuraRoute, () => {
  syncFavicon();
  syncDocumentTheme();
  scheduleRevealMotion(380);
});

watch(isMobileLayout, () => {
  nextTick().then(() => {
    syncActiveSection();
    scheduleRevealMotion(120);
  });
});

watch(currentPath, syncDocumentMeta);

onBeforeUnmount(() => {
  revealObserver?.disconnect();
  document.documentElement.classList.remove("document-theme--vecura");
  document.body.classList.remove("document-theme--vecura");

  if (revealRefreshTimer !== null) {
    window.clearTimeout(revealRefreshTimer);
  }

  window.removeEventListener("popstate", handlePopState);
  window.removeEventListener("hashchange", handlePopState);
  window.removeEventListener("scroll", syncActiveSection);
  window.removeEventListener("resize", syncActiveSection);
  document.removeEventListener("click", handleDocumentClick);
  unbindMobileLayout();
});
</script>

<style>
:root {
  --color-ink: #141414;
  --color-ink-soft: #303238;
  --color-muted: #666c73;
  --color-line: #ddd8cc;
  --color-paper: #f4f1ea;
  --color-paper-strong: #fbf8f0;
  --color-surface: #ffffff;
  --color-yellow: #d8a90d;
  --color-yellow-soft: #f7e7ad;
  --color-vecura: #00b5ed;
  --page-bg: var(--color-paper);
  --header-bg: rgba(244, 241, 234, 0.9);
  --header-border: rgba(20, 20, 20, 0.08);
  --header-text: var(--color-ink);
  --header-muted: var(--color-muted);
  --header-active-bg: rgba(20, 20, 20, 0.08);
  --primary-action-bg: var(--color-ink);
  --primary-action-border: var(--color-ink);
  --primary-action-text: #ffffff;
  --shadow-panel: 0 24px 70px rgba(31, 28, 20, 0.12);
  --site-header-height: 76px;
  --section-sticky-offset: calc(var(--site-header-height) + 32px);
}

* {
  box-sizing: border-box;
}

html {
  background: var(--color-paper);
  scroll-behavior: smooth;
}

html.document-theme--vecura {
  background: #0e151c;
}

[id] {
  scroll-margin-top: 104px;
}

body {
  margin: 0;
  background: var(--color-paper);
  color: var(--color-ink);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system,
    BlinkMacSystemFont, "Segoe UI", sans-serif;
}

body.document-theme--vecura {
  background: #0e151c;
}

button,
input,
textarea,
select {
  font: inherit;
}

a {
  color: inherit;
}

.app-frame {
  width: 100%;
  min-height: 100vh;
  margin: 0 auto;
  background: var(--page-bg);
  transition: background 520ms ease, color 520ms ease;
}

.app-frame--vecura {
  --page-bg: #0e151c;
  --header-bg: rgba(14, 21, 28, 0.9);
  --header-border: rgba(229, 230, 231, 0.12);
  --header-text: #e5e6e7;
  --header-muted: rgba(229, 230, 231, 0.64);
  --header-active-bg: rgba(0, 181, 237, 0.12);
  --primary-action-bg: var(--color-vecura);
  --primary-action-border: var(--color-vecura);
  --primary-action-text: #0e151c;
}

.section {
  margin-right: calc(50% - 50vw);
  margin-left: calc(50% - 50vw);
  padding: 86px max(20px, calc((100vw - 1180px) / 2));
}

.button-link {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 18px;
  border: 1px solid var(--color-ink);
  border-radius: 8px;
  background: var(--color-ink);
  color: #ffffff;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 0 0 rgba(31, 28, 20, 0);
  transition: background 180ms ease, border-color 180ms ease, color 180ms ease,
    box-shadow 180ms ease, transform 180ms ease;
}

.button-link:hover {
  border-color: rgba(20, 20, 20, 0.88);
  background: #242424;
  box-shadow: 0 12px 28px rgba(31, 28, 20, 0.12);
  transform: translateY(-1px);
}

.button-link--secondary {
  background: transparent;
  color: var(--color-ink);
}

.button-link--secondary:hover {
  background: rgba(20, 20, 20, 0.06);
}

.button-link--yellow {
  border-color: var(--primary-action-border);
  background: var(--primary-action-bg);
  color: var(--primary-action-text);
}

.button-link__svg-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  transition: transform 180ms ease;
}

.button-link:hover .button-link__svg-icon {
  transform: translateX(3px);
}

.motion-reveal {
  opacity: 0;
  filter: blur(4px);
  transform: translateY(18px);
  transition: opacity 620ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 620ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 620ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: calc(var(--motion-order, 0) * 36ms);
  will-change: opacity, transform, filter;
}

.motion-reveal--visible {
  opacity: 1;
  filter: blur(0);
  transform: translateY(0);
  will-change: auto;
}

.profile-focus__list article,
.responsibility__item,
.ai-expertise__grid article,
.work-modes__list article,
.evidence__list article,
.vecura-pitch__proof article,
.vecura-capabilities__grid article {
  transition: border-color 180ms ease, background 180ms ease,
    box-shadow 180ms ease, transform 180ms ease;
}

@media (hover: hover) and (pointer: fine) {
  .ai-expertise__grid article:hover,
  .contact__channel:hover {
    box-shadow: 0 16px 36px rgba(31, 28, 20, 0.08);
  }

  .responsibility__item:hover,
  .work-modes__list article:hover,
  .evidence__list article:hover,
  .profile-focus__list article:hover {
    border-color: rgba(20, 20, 20, 0.2);
  }

  .vecura-pitch__proof article:hover,
  .vecura-capabilities__grid article:hover {
    border-color: rgba(0, 181, 237, 0.34);
    background: rgba(229, 230, 231, 0.09);
    box-shadow: 0 18px 42px rgba(0, 0, 0, 0.16);
    transform: translateY(-2px);
  }
}

.page-shift-enter-active,
.page-shift-leave-active {
  transition: opacity 320ms ease, transform 320ms ease, filter 320ms ease;
}

.page-shift-enter-from {
  opacity: 0;
  filter: blur(8px);
  transform: translateY(14px);
}

.page-shift-leave-to {
  opacity: 0;
  filter: blur(8px);
  transform: translateY(-10px);
}

@media (max-width: 720px) {
  .section {
    padding-top: 64px;
    padding-bottom: 64px;
  }

  .button-link {
    min-height: 44px;
    padding-right: 15px;
    padding-left: 15px;
  }
}

@media (max-width: 760px) {
  :root {
    --site-header-height: 64px;
    --section-sticky-offset: 84px;
  }

  [id] {
    scroll-margin-top: 84px;
  }

  html,
  body {
    overflow-x: hidden;
  }

  main {
    overflow-x: clip;
  }

  .app-frame {
    padding-bottom: 82px;
    padding-bottom: calc(82px + env(safe-area-inset-bottom, 0px));
  }

  .section {
    padding-right: max(18px, calc((100vw - 1180px) / 2));
    padding-left: max(18px, calc((100vw - 1180px) / 2));
  }

  .button-link {
    min-height: 48px;
    border-radius: 14px;
  }

  .button-link__svg-icon {
    width: 17px;
    height: 17px;
  }

  .motion-reveal {
    filter: none;
    transform: translateY(12px);
  }
}

@media (max-width: 420px) {
  .section {
    padding-right: 16px;
    padding-left: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 1ms !important;
  }

  .motion-reveal {
    opacity: 1;
    filter: none;
    transform: none;
  }
}
</style>
